/**
 * V2 source path: customer/midas/components/v2/MachineThreadHistoryV2.tsx
 * V1 source: customer/midas/components/MachineThreadAccordion.tsx
 * Strategy: logic-copied / JSX-rewritten-in-Tailwind
 * Verified no-MUI: yes
 * Phase: 3
 *
 * Replaces V1's per-machine `MachineThreadAccordion` pattern. Round 4 + Round 5
 * confirmed: V2 sidebar History tab is GLOBAL across the fleet (per-machine
 * filtering lives only on the History sub-page in Phase 4). Top 20 entries
 * grouped by date eyebrows: TODAY / YESTERDAY / THIS WEEK / EARLIER.
 *
 * Each thread row: machine tag (JBM 11 #8C8375) + Sora 13 graphite title + JBM
 * 11 #8C8375 last-updated relative time on the right + optional unread dot.
 * Right-click / kebab opens a shadcn DropdownMenu with Rename + Delete actions.
 *
 * Logic copied from V1 MachineThreadAccordion.tsx:
 *   - Redux dispatches: loadThread, renameThread, deleteThread, fetchThreads.
 *   - Sort by last_message_at DESC (V1 line 224-226).
 *   - relativeTime helper (V1 lines 91-101).
 *   - Optimistic isOptimistic flag for fade-up animation on first render.
 *
 * Tap target: each row min-h-[64px] for touch comfort.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import { Thread } from "../../midasPageModels";
import {
  deleteThread,
  fetchThreads,
  loadThread,
  renameThread,
  setActiveThreadId,
} from "../../midasPageSlice";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// ════════════════════════════════════════════════════════════════
// Constants
// ════════════════════════════════════════════════════════════════

const HISTORY_TOP_N = 20;

// ════════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════════

/** Relative time string — copied from V1 MachineThreadAccordion lines 91-101. */
const relativeTime = (iso: string): string => {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
};

type DateBucket = "today" | "yesterday" | "thisWeek" | "earlier";

/** Bucket a thread by its last_message_at into one of 4 date groups. */
const bucketOf = (iso: string): DateBucket => {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 7);

  if (d.getTime() >= startOfToday.getTime()) return "today";
  if (d.getTime() >= startOfYesterday.getTime()) return "yesterday";
  if (d.getTime() >= startOfWeek.getTime()) return "thisWeek";
  return "earlier";
};

const BUCKET_LABELS: Record<DateBucket, string> = {
  today: "TODAY",
  yesterday: "YESTERDAY",
  thisWeek: "THIS WEEK",
  earlier: "EARLIER",
};

// ════════════════════════════════════════════════════════════════
// Sub-row: thread row with kebab menu
// ════════════════════════════════════════════════════════════════

interface ThreadRowProps {
  thread: Thread;
  isActive: boolean;
  machineLabel: string | null;
  onOpen: () => void;
  onRename: (id: string, currentTitle: string) => void;
  onDelete: (id: string, title: string) => void;
  prefersReducedMotion: boolean;
  shouldAnimate: boolean;
}

const ThreadRow: React.FC<ThreadRowProps> = ({
  thread,
  isActive,
  machineLabel,
  onOpen,
  onRename,
  onDelete,
  prefersReducedMotion,
  shouldAnimate,
}) => (
  <li
    className="relative"
    style={
      shouldAnimate && !prefersReducedMotion
        ? {
            animation: "midas-v2-thread-fade-in 200ms ease-out",
            animationFillMode: "backwards",
          }
        : undefined
    }
  >
    <div
      className={`flex min-h-[64px] items-start gap-2.5 rounded-md py-2.5 pr-2 transition-colors duration-150 motion-reduce:transition-none ${
        isActive
          ? "bg-white shadow-[0_1px_2px_rgba(44,38,32,0.06)]"
          : "hover:bg-[rgba(44,38,32,0.04)]"
      }`}
      style={{
        borderLeft: `3px solid ${isActive ? "#D4572B" : "transparent"}`,
        paddingLeft: "9px",
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open chat: ${
          machineLabel ? `${machineLabel} — ` : "Platform Mode — "
        }${thread.title}`}
        aria-pressed={isActive}
        className={`relative flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden text-left outline-none focus-visible:ring-2 focus-visible:ring-midas-terracotta focus-visible:ring-offset-1 focus-visible:ring-offset-midas-bone active:scale-[0.99] motion-reduce:active:scale-100 after:content-[''] after:pointer-events-none after:absolute after:top-0 after:right-0 after:bottom-0 after:w-20 after:bg-gradient-to-l after:to-transparent ${
          isActive ? "after:from-white" : "after:from-midas-bone"
        }`}
      >
        {machineLabel ? (
          <span
            className="block w-full overflow-hidden whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.06em] text-midas-muted-stone"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            {machineLabel}
          </span>
        ) : (
          <span
            className="flex w-full items-center gap-1 overflow-hidden whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.06em] text-midas-muted-stone"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <span
              aria-hidden="true"
              className="inline-block h-[6px] w-[6px] flex-shrink-0 rounded-full bg-midas-terracotta"
            />
            <span>Platform Mode</span>
          </span>
        )}
        <span
          className="block w-full overflow-hidden whitespace-nowrap font-sora text-[13px] font-medium text-midas-graphite"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {thread.title}
        </span>
      </button>
      <div className="flex flex-shrink-0 items-center gap-1.5">
        <span
          className="font-mono text-[11px] text-midas-muted-stone"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {relativeTime(thread.last_message_at || thread.created_at)}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Thread actions"
              className="flex h-11 w-11 items-center justify-center rounded-md text-midas-muted-stone outline-none transition-colors duration-150 hover:bg-[rgba(44,38,32,0.06)] hover:text-midas-warm-graphite focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
            >
              <MoreVertical className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={4}
            className="min-w-[140px] border-[rgba(44,38,32,0.08)] bg-white"
          >
            <DropdownMenuItem
              onSelect={() => onRename(thread.id, thread.title)}
              className="gap-2 font-sora text-[13px] text-midas-graphite focus:bg-midas-bone-light"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onDelete(thread.id, thread.title)}
              className="gap-2 font-sora text-[13px] text-[#C0392B] focus:bg-[rgba(192,57,43,0.06)]"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  </li>
);

// ════════════════════════════════════════════════════════════════
// Inline rename / delete confirm prompts
// ════════════════════════════════════════════════════════════════

interface PendingRename {
  id: string;
  title: string;
}

interface PendingDelete {
  id: string;
  title: string;
}

// ════════════════════════════════════════════════════════════════
// MachineThreadHistoryV2
// ════════════════════════════════════════════════════════════════

interface MachineThreadHistoryV2Props {
  /** Called AFTER a thread row is opened — used by phone overlay to dismiss the drawer. */
  onAfterOpen?: () => void;
  /** Whether the user has prefers-reduced-motion turned on. Disables row entry animations. */
  prefersReducedMotion?: boolean;
}

const MachineThreadHistoryV2: React.FC<MachineThreadHistoryV2Props> = ({
  onAfterOpen,
  prefersReducedMotion = false,
}) => {
  const dispatch = useAppDispatch();

  const threads = useAppSelector((s) => s.midasPage.threads);
  const threadsLoading = useAppSelector((s) => s.midasPage.threadsLoading);
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);

  // Build a quick id -> machine name lookup for the per-row machine tag.
  const machineNameById = useMemo(() => {
    const map: Record<string, string> = {};
    if (machinesMap && typeof machinesMap === "object") {
      Object.values(machinesMap).forEach((group) => {
        if (Array.isArray(group)) {
          (group as Array<{ id: string; model?: string; customBrandName?: string; manufacturer?: { name?: string } }>).forEach(
            (m) => {
              const brand = m.customBrandName || m.manufacturer?.name || "";
              const name = brand
                ? `${brand} ${m.model || ""}`.trim()
                : m.model || "";
              if (m.id && name) map[m.id] = name;
            }
          );
        }
      });
    }
    return map;
  }, [machinesMap]);

  // Track threads that have already been animated so re-renders don't replay.
  const animatedIdsRef = useRef<Set<string>>(new Set());

  // Top-20 sort + bucket
  const grouped = useMemo(() => {
    const sorted = [...threads]
      .filter((t) => !!t)
      .sort(
        (a, b) =>
          new Date(b.last_message_at || b.created_at).getTime() -
          new Date(a.last_message_at || a.created_at).getTime()
      )
      .slice(0, HISTORY_TOP_N);

    const buckets: Record<DateBucket, Thread[]> = {
      today: [],
      yesterday: [],
      thisWeek: [],
      earlier: [],
    };
    sorted.forEach((t) => {
      const b = bucketOf(t.last_message_at || t.created_at);
      buckets[b].push(t);
    });
    return buckets;
  }, [threads]);

  // Inject row entry keyframes once
  useEffect(() => {
    if (typeof document === "undefined") return;
    const STYLE_ID = "midas-v2-history-anim";
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      @keyframes midas-v2-thread-fade-in {
        from { opacity: 0; transform: translateY(-4px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Lazy-load global threads if the list is empty
  useEffect(() => {
    if (threads.length === 0 && !threadsLoading) {
      dispatch(fetchThreads({ limit: 50, offset: 0 }));
    }
  }, [threads.length, threadsLoading, dispatch]);

  const [pendingRename, setPendingRename] = useState<PendingRename | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const handleOpen = useCallback(
    (id: string) => {
      if (id === activeThreadId) return;
      dispatch(loadThread(id));
      if (onAfterOpen) onAfterOpen();
    },
    [activeThreadId, dispatch, onAfterOpen]
  );

  const handleStartRename = useCallback((id: string, title: string) => {
    setPendingRename({ id, title });
  }, []);

  const handleConfirmRename = useCallback(
    (newTitle: string) => {
      if (pendingRename && newTitle.trim() && newTitle.trim() !== pendingRename.title) {
        dispatch(renameThread({ id: pendingRename.id, title: newTitle.trim() }));
      }
      setPendingRename(null);
    },
    [dispatch, pendingRename]
  );

  const handleStartDelete = useCallback((id: string, title: string) => {
    setPendingDelete({ id, title });
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (pendingDelete) {
      dispatch(deleteThread(pendingDelete.id));
      if (pendingDelete.id === activeThreadId) {
        dispatch(setActiveThreadId(null));
      }
    }
    setPendingDelete(null);
  }, [activeThreadId, dispatch, pendingDelete]);

  const totalShown =
    grouped.today.length +
    grouped.yesterday.length +
    grouped.thisWeek.length +
    grouped.earlier.length;

  if (threadsLoading && threads.length === 0) {
    return (
      <div
        id="midas-sidebar-history-panel"
        role="tabpanel"
        aria-label="Conversation history"
        className="px-2 pb-3"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex min-h-[64px] items-start gap-2.5 rounded-md px-3 py-2.5"
          >
            <div className="flex-1 space-y-1.5">
              <div className="h-2.5 w-20 animate-pulse rounded bg-midas-stone/70" />
              <div className="h-3 w-44 animate-pulse rounded bg-midas-stone" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (totalShown === 0) {
    return (
      <div
        id="midas-sidebar-history-panel"
        role="tabpanel"
        aria-label="Conversation history"
        className="px-3 py-6 text-center"
      >
        <p
          className="font-sora text-[12px] text-midas-muted-stone"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          No conversations yet.
        </p>
      </div>
    );
  }

  const renderBucket = (bucket: DateBucket) => {
    const list = grouped[bucket];
    if (list.length === 0) return null;
    return (
      <section key={bucket} aria-label={BUCKET_LABELS[bucket]}>
        <h3
          className="px-3 pb-1 pt-3 font-mono text-[11px] uppercase tracking-[0.12em] text-midas-muted-stone"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {BUCKET_LABELS[bucket]}
        </h3>
        <ul className="flex flex-col gap-0.5">
          {list.map((t) => {
            const isActive = t.id === activeThreadId;
            const machineLabel = t.machine_id
              ? machineNameById[t.machine_id] || null
              : null;
            const shouldAnimate =
              !!t.isOptimistic && !animatedIdsRef.current.has(t.id);
            if (shouldAnimate) animatedIdsRef.current.add(t.id);
            return (
              <ThreadRow
                key={t.id}
                thread={t}
                isActive={isActive}
                machineLabel={machineLabel}
                onOpen={() => handleOpen(t.id)}
                onRename={handleStartRename}
                onDelete={handleStartDelete}
                prefersReducedMotion={prefersReducedMotion}
                shouldAnimate={shouldAnimate}
              />
            );
          })}
        </ul>
      </section>
    );
  };

  return (
    <>
      <div
        id="midas-sidebar-history-panel"
        role="tabpanel"
        aria-label="Conversation history"
        className="flex flex-col px-2 pb-3"
        style={{ width: '100%', minWidth: 0, display: 'flow-root' }}
      >
        {renderBucket("today")}
        {renderBucket("yesterday")}
        {renderBucket("thisWeek")}
        {renderBucket("earlier")}
      </div>
      {/* Inline rename prompt — overlay since shadcn Dialog already in V2 dock */}
      {pendingRename && (
        <RenamePrompt
          initialTitle={pendingRename.title}
          onCancel={() => setPendingRename(null)}
          onConfirm={handleConfirmRename}
        />
      )}
      {pendingDelete && (
        <DeleteConfirmPrompt
          title={pendingDelete.title}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
};

// ════════════════════════════════════════════════════════════════
// Inline rename prompt (Tailwind, MUI-free, replaces V1 MUI Dialog)
// ════════════════════════════════════════════════════════════════

const RenamePrompt: React.FC<{
  initialTitle: string;
  onCancel: () => void;
  onConfirm: (title: string) => void;
}> = ({ initialTitle, onCancel, onConfirm }) => {
  const [value, setValue] = useState(initialTitle);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Rename conversation"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-[360px] rounded-lg bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.10)]"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className="mb-3 font-sora text-[14px] font-semibold text-midas-graphite"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          Rename conversation
        </h3>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onConfirm(value);
            if (e.key === "Escape") onCancel();
          }}
          maxLength={120}
          className="block h-11 w-full rounded-md border border-midas-stone bg-midas-bone-light px-3 text-[16px] text-midas-graphite outline-none focus:border-midas-terracotta focus:ring-2 focus:ring-midas-terracotta/30"
          style={{ fontFamily: "'Sora', sans-serif" }}
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-11 rounded-md px-4 font-sora text-[13px] font-medium text-midas-warm-graphite outline-none transition-colors hover:bg-midas-bone-light focus-visible:ring-2 focus-visible:ring-midas-terracotta"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(value)}
            disabled={!value.trim()}
            className="h-11 rounded-md bg-midas-terracotta px-4 font-sora text-[13px] font-semibold text-white outline-none transition-colors hover:bg-[#B84A24] focus-visible:ring-2 focus-visible:ring-midas-terracotta disabled:opacity-50"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmPrompt: React.FC<{
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ title, onCancel, onConfirm }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-label="Delete conversation"
    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
    onClick={onCancel}
  >
    <div
      className="w-full max-w-[360px] rounded-lg bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.10)]"
      onClick={(e) => e.stopPropagation()}
    >
      <h3
        className="mb-2 font-sora text-[14px] font-semibold text-midas-graphite"
        style={{ fontFamily: "'Sora', sans-serif" }}
      >
        Delete conversation?
      </h3>
      <p
        className="mb-4 font-sora text-[13px] text-midas-warm-graphite"
        style={{ fontFamily: "'Sora', sans-serif" }}
      >
        &ldquo;{title}&rdquo; will be removed permanently. This cannot be undone.
      </p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-11 rounded-md px-4 font-sora text-[13px] font-medium text-midas-warm-graphite outline-none transition-colors hover:bg-midas-bone-light focus-visible:ring-2 focus-visible:ring-midas-terracotta"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="h-11 rounded-md bg-[#C0392B] px-4 font-sora text-[13px] font-semibold text-white outline-none transition-colors hover:bg-[#A12F23] focus-visible:ring-2 focus-visible:ring-[#C0392B]"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

export default MachineThreadHistoryV2;
